/**
 * NocturneEphem — low-precision ephemeris (Paul Schlyter's method): Sun, Moon and the five naked-eye
 * planets to a few arcminutes, plus horizon coordinates, rise/transit/set and twilight for any place.
 * Accurate enough for a sky map and "visible tonight from 21 h 10" — not for pointing a telescope.
 */
(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  if (root.NocturneEphem) return;

  const RAD = Math.PI / 180;
  const sin = (d) => Math.sin(d * RAD);
  const cos = (d) => Math.cos(d * RAD);
  const atan2 = (y, x) => Math.atan2(y, x) / RAD;
  const norm = (d) => ((d % 360) + 360) % 360;

  // Days since 2000 Jan 0.0 UT.
  const dayNumber = (t) => t / 86400000 - 10956;

  // Orbital elements as functions of the day number: N, i, w, a, e, M.
  const ELEMENTS = {
    mercury: (d) => [48.3313 + 3.24587e-5 * d, 7.0047 + 5e-8 * d, 29.1241 + 1.01444e-5 * d, 0.387098, 0.205635 + 5.59e-10 * d, 168.6562 + 4.0923344368 * d],
    venus: (d) => [76.6799 + 2.4659e-5 * d, 3.3946 + 2.75e-8 * d, 54.891 + 1.38374e-5 * d, 0.72333, 0.006773 - 1.302e-9 * d, 48.0052 + 1.6021302244 * d],
    mars: (d) => [49.5574 + 2.11081e-5 * d, 1.8497 - 1.78e-8 * d, 286.5016 + 2.92961e-5 * d, 1.523688, 0.093405 + 2.516e-9 * d, 18.6021 + 0.5240207766 * d],
    jupiter: (d) => [100.4542 + 2.76854e-5 * d, 1.303 - 1.557e-7 * d, 273.8777 + 1.64505e-5 * d, 5.20256, 0.048498 + 4.469e-9 * d, 19.895 + 0.0830853001 * d],
    saturn: (d) => [113.6634 + 2.3898e-5 * d, 2.4886 - 1.081e-7 * d, 339.3939 + 2.97661e-5 * d, 9.55475, 0.055546 - 9.499e-9 * d, 316.967 + 0.0334442282 * d],
  };

  function kepler(M, e) {
    let E = M + (e / RAD) * sin(M) * (1 + e * cos(M));
    for (let k = 0; k < 6; k++) E -= (E - (e / RAD) * sin(E) - M) / (1 - e * cos(E));
    return E;
  }

  // Position in the orbit plane, rotated to ecliptic x/y/z.
  function orbit([N, i, w, a, e, M]) {
    const E = kepler(norm(M), e);
    const xv = a * (cos(E) - e);
    const yv = a * Math.sqrt(1 - e * e) * sin(E);
    const v = atan2(yv, xv);
    const r = Math.hypot(xv, yv);
    return {
      x: r * (cos(N) * cos(v + w) - sin(N) * sin(v + w) * cos(i)),
      y: r * (sin(N) * cos(v + w) + cos(N) * sin(v + w) * cos(i)),
      z: r * sin(v + w) * sin(i),
      r,
    };
  }

  function sun(d) {
    const w = 282.9404 + 4.70935e-5 * d;
    const e = 0.016709 - 1.151e-9 * d;
    const M = norm(356.047 + 0.9856002585 * d);
    const E = kepler(M, e);
    const xv = cos(E) - e;
    const yv = Math.sqrt(1 - e * e) * sin(E);
    const lon = norm(atan2(yv, xv) + w);
    const r = Math.hypot(xv, yv);
    return { lon, lat: 0, r, x: r * cos(lon), y: r * sin(lon), z: 0, M, L: norm(M + w) };
  }

  function moon(d, s) {
    const N = 125.1228 - 0.0529538083 * d;
    const w = 318.0634 + 0.1643573223 * d;
    const M = norm(115.3654 + 13.0649929509 * d);
    const p = orbit([N, 5.1454, w, 60.2666, 0.0549, M]);
    let lon = atan2(p.y, p.x);
    let lat = atan2(p.z, Math.hypot(p.x, p.y));
    let r = p.r;
    // Main perturbations (evection, variation, yearly equation…).
    const Ms = s.M, Mm = M, Lm = norm(M + w + N), D = Lm - s.L, F = Lm - N;
    lon += -1.274 * sin(Mm - 2 * D) + 0.658 * sin(2 * D) - 0.186 * sin(Ms) - 0.059 * sin(2 * Mm - 2 * D)
      - 0.057 * sin(Mm - 2 * D + Ms) + 0.053 * sin(Mm + 2 * D) + 0.046 * sin(2 * D - Ms) + 0.041 * sin(Mm - Ms)
      - 0.035 * sin(D) - 0.031 * sin(Mm + Ms) - 0.015 * sin(2 * F - 2 * D) + 0.011 * sin(Mm - 4 * D);
    lat += -0.173 * sin(F - 2 * D) - 0.055 * sin(Mm - F - 2 * D) - 0.046 * sin(Mm + F - 2 * D) + 0.033 * sin(F + 2 * D) + 0.017 * sin(2 * Mm + F);
    r += -0.58 * cos(Mm - 2 * D) - 0.46 * cos(2 * D);
    return { lon: norm(lon), lat, r };
  }

  function planet(name, d, s) {
    const el = ELEMENTS[name](d);
    const h = orbit(el);
    let lon = atan2(h.y, h.x);
    let lat = atan2(h.z, Math.hypot(h.x, h.y));
    if (name === 'jupiter' || name === 'saturn') {
      const Mj = norm(ELEMENTS.jupiter(d)[5]);
      const Ms = norm(ELEMENTS.saturn(d)[5]);
      if (name === 'jupiter') {
        lon += -0.332 * sin(2 * Mj - 5 * Ms - 67.6) - 0.056 * sin(2 * Mj - 2 * Ms + 21) + 0.042 * sin(3 * Mj - 5 * Ms + 21)
          - 0.036 * sin(Mj - 2 * Ms) + 0.022 * cos(Mj - Ms) + 0.023 * sin(2 * Mj - 3 * Ms + 52) - 0.016 * sin(Mj - 5 * Ms - 69);
      } else {
        lon += 0.812 * sin(2 * Mj - 5 * Ms - 67.6) - 0.229 * cos(2 * Mj - 4 * Ms - 2) + 0.119 * sin(Mj - 2 * Ms - 3)
          + 0.046 * sin(2 * Mj - 6 * Ms - 69) + 0.014 * sin(Mj - 3 * Ms + 32);
        lat += -0.02 * cos(2 * Mj - 4 * Ms - 2) + 0.018 * sin(2 * Mj - 6 * Ms - 49);
      }
    }
    // Heliocentric → geocentric.
    const x = h.r * cos(lon) * cos(lat) + s.x;
    const y = h.r * sin(lon) * cos(lat) + s.y;
    const z = h.r * sin(lat);
    return { lon: norm(atan2(y, x)), lat: atan2(z, Math.hypot(x, y)), r: Math.hypot(x, y, z) };
  }

  // Ecliptic longitude/latitude → right ascension/declination (degrees).
  function equatorial(lon, lat, d) {
    const ecl = 23.4393 - 3.563e-7 * d;
    const x = cos(lon) * cos(lat);
    const y = sin(lon) * cos(lat);
    const z = sin(lat);
    const ye = y * cos(ecl) - z * sin(ecl);
    const ze = y * sin(ecl) + z * cos(ecl);
    return { ra: norm(atan2(ye, x)), dec: atan2(ze, Math.hypot(x, ye)) };
  }

  /* Everything at instant t (ms): RA/Dec of the Sun, Moon and planets, the Moon's phase, local sidereal angle. */
  function sky(t) {
    const d = dayNumber(t);
    const s = sun(d);
    const m = moon(d, s);
    const out = { sun: equatorial(s.lon, 0, d), moon: { ...equatorial(m.lon, m.lat, d), dist: m.r } };
    for (const name of Object.keys(ELEMENTS)) {
      const p = planet(name, d, s);
      out[name] = { ...equatorial(p.lon, p.lat, d), dist: p.r };
    }
    const elong = norm(m.lon - s.lon); // 0 new, 180 full
    out.phase = { frac: elong / 360, illum: (1 - cos(elong)) / 2, waxing: elong < 180 };
    const ut = ((t % 86400000) + 86400000) % 86400000 / 3600000;
    out.gmst = norm(s.L + 180 + ut * 15);
    return out;
  }

  /* Horizon coordinates: altitude and azimuth (0 = north, 90 = east), in degrees. */
  function horizon(ra, dec, gmst, lat, lon) {
    const ha = gmst + lon - ra;
    const x = cos(ha) * cos(dec);
    const y = sin(ha) * cos(dec);
    const z = sin(dec);
    const xh = x * sin(lat) - z * cos(lat);
    const zh = x * cos(lat) + z * sin(lat);
    return { alt: Math.asin(Math.max(-1, Math.min(1, zh))) / RAD, az: norm(atan2(y, xh) + 180) };
  }

  /* Altitude/azimuth of a body ('sun', 'moon', a planet) or of {ra, dec} at instant t. */
  function position(body, t, lat, lon, s = sky(t)) {
    const eq = typeof body === 'string' ? s[body] : body;
    const h = horizon(eq.ra, eq.dec, s.gmst, lat, lon);
    if (body === 'moon') h.alt -= (Math.asin(1 / eq.dist) / RAD) * cos(h.alt); // parallax: up to 1°
    return h;
  }

  /**
   * Samples one night (noon to noon around t) every `step` minutes and returns the Sun's events and,
   * for each requested body, when it rises, sets, culminates and is usable in a dark sky.
   */
  function night(t, lat, lon, bodies, { step = 5, tz = 'Europe/Paris' } = {}) {
    const start = localNoon(t, tz);
    const n = Math.round((24 * 60) / step);
    const times = [];
    const skies = [];
    for (let k = 0; k <= n; k++) {
      const tk = start + k * step * 60000;
      times.push(tk);
      skies.push(sky(tk));
    }
    const sunAlt = skies.map((s, k) => position('sun', times[k], lat, lon, s).alt);
    const cross = (alts, h, dir) => {
      for (let k = 1; k < alts.length; k++) {
        if (dir > 0 ? alts[k - 1] < h && alts[k] >= h : alts[k - 1] >= h && alts[k] < h) {
          const f = (h - alts[k - 1]) / (alts[k] - alts[k - 1]);
          return times[k - 1] + f * (times[k] - times[k - 1]);
        }
      }
      return null;
    };
    const out = {
      start,
      end: times[n],
      sunset: cross(sunAlt, -0.833, -1),
      sunrise: cross(sunAlt, -0.833, 1),
      dusk: cross(sunAlt, -12, -1), // nautical: dark enough for planets and bright stars
      dawn: cross(sunAlt, -12, 1),
      darkStart: cross(sunAlt, -18, -1), // astronomical: truly dark
      darkEnd: cross(sunAlt, -18, 1),
      bodies: {},
    };
    for (const body of bodies) {
      const pos = skies.map((s, k) => position(typeof body === 'string' ? body : body.eq, times[k], lat, lon, s));
      const alts = pos.map((p) => p.alt);
      const h0 = body === 'moon' ? 0.125 : -0.5667;
      // Usable: the Sun below -12° and the object at least 10° up.
      let from = null, to = null, best = null;
      alts.forEach((a, k) => {
        if (sunAlt[k] < -12 && a > 10) {
          if (from === null) from = times[k];
          to = times[k];
          if (!best || a > best.alt) best = { t: times[k], alt: a, az: pos[k].az };
        }
      });
      // A good moment that is not necessarily the highest: the first dark time it stands well up.
      let good = null;
      if (best) {
        const th = Math.min(30, best.alt - 3);
        const k = alts.findIndex((a, j) => sunAlt[j] < -12 && a >= th);
        if (k >= 0) good = { t: times[k], alt: alts[k], az: pos[k].az };
      }
      out.bodies[typeof body === 'string' ? body : body.id] = {
        rise: cross(alts, h0, 1),
        set: cross(alts, h0, -1),
        from,
        to,
        best,
        good,
      };
    }
    return out;
  }

  // The most recent local noon before t (so "tonight" at 2 a.m. is still the night in progress).
  function localNoon(t, tz) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date(t))
        .map((p) => [p.type, p.value]),
    );
    const offset = tzOffset(t, tz);
    let noon = Date.UTC(+parts.year, +parts.month - 1, +parts.day, 12) - offset;
    if (+parts.hour < 12) noon -= 86400000;
    return noon;
  }

  // Minutes east of UTC for a time zone at instant t, in ms.
  function tzOffset(t, tz) {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date(t))
        .map((x) => [x.type, x.value]),
    );
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(t / 1000) * 1000;
  }

  /* Angle between two {ra, dec} points, in degrees. */
  function separation(a, b) {
    const c = sin(a.dec) * sin(b.dec) + cos(a.dec) * cos(b.dec) * cos(a.ra - b.ra);
    return Math.acos(Math.max(-1, Math.min(1, c))) / RAD;
  }

  root.NocturneEphem = { sky, horizon, position, night, localNoon, tzOffset, separation };
})();
